import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { BehaviorSubject, Observable, tap, catchError, map, of, filter, throwError, take, switchMap } from 'rxjs';
import { PaginationService } from '../pagination.service';
import { Site } from '../../objects/site';

@Injectable({
  providedIn: 'root'
})
export class SiteService {
  private http = inject(HttpClient);
  private paginationService = inject(PaginationService);

  private allSitesSubject = new BehaviorSubject<Site[]>([]);
  public allSites$ = this.allSitesSubject.asObservable();
  private isCacheRefreshedSubject = new BehaviorSubject<boolean>(false);
  public isCacheRefreshed$ = this.isCacheRefreshedSubject.asObservable();

  constructor() {
    this.refreshAllSitesRecords().subscribe({
      next: () => this.isCacheRefreshedSubject.next(true),
      error: (err) => {
        console.error('Initial site cache refresh failed.', err);
        this.isCacheRefreshedSubject.next(false);
      }
    });
  }

  /**
   * Modern, reactive function to write a Site object to the database.
   * Replaces the old Promise-based approach with RxJS.
   * @param site The Site object to write.
   * @returns An observable of the API response.
   */
public writeSite(site: Site): Observable<any> {
  const jsonReadyBody = {
    'siteNum': site.siteNum,
    'siteData': site,
  };

  const options = {
    headers: {
      'Content-Type': 'application/json'
    }
  };

  return this.http.put(environment.siteWriteUrl, jsonReadyBody, options).pipe(
    tap(response => {
      console.log('Successfully wrote site to database.');
    }),
    tap(() => {
      this.refreshAllSitesRecords().subscribe();
    }),
    catchError(error => {
      console.error('❌ HTTP ERROR:', error);
      console.error('❌ Error status:', error.status);
      console.error('❌ Error message:', error.message);
      console.error('An error occurred while writing the site:', error);
      return throwError(() => new Error('Could not write site to the database.'));
    })
  );
}



  /**
   * Gets a specific site object from the in-memory cache based on its ID or name.
   * @param siteIdOrName The unique ID or name of the site.
   * @returns An observable that emits the site object or null if not found.
   */
  public getSiteObject(siteIdOrName: string): Observable<Site | null> {
    return this.allSites$.pipe(
      map(sites => {
        const foundSite = sites.find(site => 
          site.siteNum === siteIdOrName || site.name === siteIdOrName
        );
        return foundSite || null;
      }),
      take(1)
    );
  }

  /**
   * Gets a specific site friendly name from the in-memory cache based on its ID.
   * @param siteId The unique ID of the site.
   * @returns An observable that emits the site's friendly name.
   */
  public getSiteFriendlyName(siteId: string): Observable<string | null> {
    return this.allSites$.pipe(
      map(sites => {
        const foundSite = sites.find(site => site.siteNum === siteId);
        return foundSite?.name ?? null;
      }),
      take(1)
    );
  }

  /**
   * Gets a fully-hydrated Site object by its ID.
   * @param siteId The ID of the Site to retrieve.
   * @returns An observable of the fully-hydrated Site or null if not found.
   */
  public getSiteObjectFullyHydrated(siteId: string): Observable<Site | null> {
    return this.isCacheRefreshed$.pipe(
      filter(isRefreshed => isRefreshed),
      take(1),
      switchMap(() => this.getSiteObject(siteId)),
      switchMap(site => {
        if (!site) {
          return of(null);
        }
        // Future: Add hydration logic here when connections are ready
        // return this.connectionsService.hydrateEntity(site, propertyMap);
        return of(site); // Returning the un-hydrated site for now
      }),
      catchError(error => {
        console.error('Error fetching Site object:', error);
        return of(null);
      })
    );
  }

  /**
   * Private function to fetch a single page of sites from the API.
   * This is used by the pagination service.
   * @param lastEvaluatedKey The key for the next page of results.
   */
  private getPageOfSites(lastEvaluatedKey: any = null): Observable<any> {
    const jsonReadyBody = {
      'lastEvaluatedKey': lastEvaluatedKey
    };

    const options = {
      headers: { 'Content-Type': 'application/json' }
    };

    return this.http.post(environment.sitesPaginationUrl, jsonReadyBody, options).pipe(
      // tap(response => console.log('Fetched site page:', response)),
      map((response: any) => ({
        items: response.sites || [],
        lastEvaluatedKey: response.lastEvaluatedKey
      })),
      catchError(error => {
        console.error('Error fetching site page:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Refreshes all sites by calling the paginated API and updating the subject.
   * Includes logic to mark inactive sites and process site data.
   */
  public refreshAllSitesRecords(): Observable<Site[]> {
    return this.paginationService.getAllPaginatedDataProgressively(key => this.getPageOfSites(key))
      .pipe(
        map(allSitesRaw => {
          // Process sites similar to the original logic
          const processedSites: Site[] = [];
          
          allSitesRaw.forEach((siteResponse: any) => {
            if (siteResponse.siteData) {
              const siteData = { ...siteResponse.siteData };
              
              // Mark inactive sites
              if (siteData.inactive) {
                siteData.name = siteData.name + ' (INACTIVE)';
              }
              
              processedSites.push(siteData);
            }
          });
          
          return processedSites;
        }),
        // tap(siteList => console.log('Successfully fetched all sites:', siteList)),
        tap(siteList => this.allSitesSubject.next(siteList)),
        catchError(error => {
          console.error('Error fetching all sites:', error);
          this.allSitesSubject.next([]);
          return throwError(() => error);
        })
      );
  }

  /**
   * Forces the allSites$ observable to refresh its data from the API.
   * Components or services can call this method to invalidate the cache.
   */
  public refreshAllSitesCache(): void {
    this.refreshAllSitesRecords().subscribe();
  }



  /**
   * Gets active sites only (excludes inactive sites).
   * @returns An observable of active sites.
   */
  public getActiveSites(): Observable<Site[]> {
    return this.allSites$.pipe(
      map(sites => sites.filter(site => !site.inactive))
    );
  }

  /**
   * Gets inactive sites only.
   * @returns An observable of inactive sites.
   */
  public getInactiveSites(): Observable<Site[]> {
    return this.allSites$.pipe(
      map(sites => sites.filter(site => site.inactive))
    );
  }

  /**
   * Searches sites by name (case-insensitive partial match).
   * @param searchTerm The term to search for.
   * @returns An observable of matching sites.
   */
  public searchSitesByName(searchTerm: string): Observable<Site[]> {
    if (!searchTerm) {
      return this.allSites$;
    }

    return this.allSites$.pipe(
      map(sites => 
        sites.filter(site => 
          site.name?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
    );
  }
}